import { useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import Breadcrumb from '@/components/base/Breadcrumb';
import { siteFaqs } from '@/mocks/faq';

const FORM_SUBMIT_URL = 'https://readdy.ai/api/form/d9p1k0qftmtnfcfh0fv0';

export default function AboutPage() {
  const [openFaq, setOpenFaq] = useState<string | null>(null);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formErrorMsg, setFormErrorMsg] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');
    setFormErrorMsg('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    // Honeypot check
    const honeypotValue = (formData.get('phone_alt') as string || '').trim();
    if (honeypotValue) {
      setFormStatus('success');
      form.reset();
      return;
    }
    formData.delete('phone_alt');

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, value as string);
      });

      const response = await fetch(FORM_SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (response.ok && parsed && (parsed as Record<string, unknown>).code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg =
          (parsed && (parsed as Record<string, unknown>).meta && ((parsed as Record<string, unknown>).meta as Record<string, unknown>).message as string) ||
          (parsed && (parsed as Record<string, unknown>).message as string) ||
          '提交失败，请稍后重试';
        setFormStatus('error');
        setFormErrorMsg(serverMsg);
      }
    } catch {
      setFormStatus('error');
      setFormErrorMsg('网络错误，请检查连接后重试');
    }
  }, []);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '关于我们' },
  ];

  const schemaData = [
    {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: '关于韩漫屋',
      description: '韩漫屋是官方认证的韩漫官网站，提供权威的韩漫大全与好看韩国漫画免费在线观看。',
      url: 'https://www.hanmanwu.com/about',
      inLanguage: 'zh-CN',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: '韩漫屋',
      url: 'https://www.hanmanwu.com',
      description: '韩漫屋是官方认证的韩漫官网站，提供权威的韩漫大全与好看韩国漫画免费在线观看。',
      foundingDate: '2020',
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer service',
        email: 'service@hanmanwu.com',
        availableLanguage: ['Chinese'],
      },
      address: {
        '@type': 'PostalAddress',
        addressLocality: '首尔',
        addressRegion: '首尔特别市',
        addressCountry: 'KR',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: siteFaqs.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer,
        },
      })),
    },
  ];

  const features = [
    {
      icon: 'ri-shield-check-line',
      title: '官方认证',
      desc: '韩漫屋是官方认证的韩漫官网站，所有漫画资源均经过正版授权，确保内容质量与合法性，为用户提供放心的阅读环境。',
    },
    {
      icon: 'ri-image-line',
      title: '高清画质',
      desc: '采用先进的图片优化技术，所有韩漫均为高清画质呈现，画面清晰细腻，色彩还原准确，带来沉浸式的视觉享受。',
    },
    {
      icon: 'ri-flashlight-line',
      title: '极速加载',
      desc: '全球CDN加速节点覆盖，智能图片预加载技术，确保漫画秒开秒看，即使在移动网络下也能流畅阅读。',
    },
    {
      icon: 'ri-smartphone-line',
      title: '多端适配',
      desc: '响应式设计完美适配电脑、平板、手机等设备，随时随地畅享韩漫阅读，不错过每一话的精彩内容。',
    },
    {
      icon: 'ri-time-line',
      title: '每日更新',
      desc: '与韩国各大漫画平台保持同步更新，连载漫画每周准时更新，热门作品更可实现多话连更，追更零等待。',
    },
    {
      icon: 'ri-user-heart-line',
      title: '用户至上',
      desc: '500万+读者的共同选择，持续优化阅读体验，用心倾听每一位用户的声音，努力打造最优质的韩漫阅读平台。',
    },
  ];

  const stats = [
    { value: '15,000+', label: '海量韩漫资源' },
    { value: '10+', label: '漫画分类' },
    { value: '500万+', label: '活跃读者' },
    { value: '99.9%', label: '服务稳定性' },
  ];

  return (
    <>
      <SEO
        title="关于我们_韩漫屋-权威韩漫官网站_品牌介绍"
        description="韩漫屋是官方认证的韩漫官网站，提供权威的韩漫大全与好看韩国漫画免费在线观看。了解更多关于韩漫屋的品牌介绍、平台优势及联系方式。"
        keywords="韩漫屋,关于韩漫屋,韩漫官网,韩国漫画平台,韩漫品牌介绍"
        canonicalUrl="https://www.hanmanwu.com/about"
        ogType="website"
        schemaJsonLd={schemaData}
      />

      <main className="min-h-screen pt-20 pb-10">
        <div className="max-w-7xl mx-auto">
          <Breadcrumb items={breadcrumbItems} />

          <section className="px-4 md:px-6 py-10 md:py-14" aria-labelledby="about-heading">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <h1 id="about-heading" className="text-3xl md:text-4xl font-heading text-foreground-950 mb-4">
                关于韩漫屋
              </h1>
              <p className="text-base text-foreground-600 leading-relaxed">
                韩漫屋成立于2020年，是官方认证的韩漫官网站。我们致力于为广大漫画爱好者提供权威的韩漫大全与好看韩国漫画免费在线观看服务，收录海量热门韩国漫画资源，涵盖恋爱、奇幻、动作、悬疑、搞笑、古风、都市等多种类型，每日更新最新章节，确保资源质量与阅读体验。
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-16">
              {stats.map((stat) => (
                <div key={stat.label} className="text-center p-6 bg-background-100 rounded-lg">
                  <p className="text-2xl md:text-3xl font-bold text-primary-500">{stat.value}</p>
                  <p className="text-sm text-foreground-500 mt-2">{stat.label}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100" aria-labelledby="features-heading">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-10">
                <h2 id="features-heading" className="text-2xl md:text-3xl font-heading text-foreground-950 mb-3">
                  平台优势
                </h2>
                <p className="text-sm text-foreground-500">为什么选择韩漫屋？六大核心优势为您保驾护航</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {features.map((feature) => (
                  <div
                    key={feature.title}
                    className="bg-background-50 border border-background-200/70 rounded-lg p-6 hover:border-primary-200 transition-all duration-300"
                  >
                    <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500 mb-4">
                      <i className={`${feature.icon} text-xl`}></i>
                    </div>
                    <h3 className="text-base font-medium text-foreground-900 mb-2">{feature.title}</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{feature.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="px-4 md:px-6 py-10 md:py-14" aria-labelledby="contact-heading">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-10">
                <h2 id="contact-heading" className="text-2xl md:text-3xl font-heading text-foreground-950 mb-3">
                  联系我们
                </h2>
                <p className="text-sm text-foreground-500">有任何问题或建议？欢迎通过以下方式与我们取得联系</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
                <div className="bg-background-100 rounded-lg p-6 text-center">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-50 text-primary-500 mx-auto mb-4">
                    <i className="ri-mail-line text-xl"></i>
                  </div>
                  <h3 className="text-sm font-medium text-foreground-900 mb-1">电子邮箱</h3>
                  <p className="text-sm text-foreground-600">service@hanmanwu.com</p>
                  <p className="text-xs text-foreground-400 mt-1">24小时内回复</p>
                </div>

                <div className="bg-background-100 rounded-lg p-6 text-center">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-50 text-primary-500 mx-auto mb-4">
                    <i className="ri-customer-service-line text-xl"></i>
                  </div>
                  <h3 className="text-sm font-medium text-foreground-900 mb-1">在线客服</h3>
                  <p className="text-sm text-foreground-600">工作日 9:00 - 18:00</p>
                  <p className="text-xs text-foreground-400 mt-1">节假日休息</p>
                </div>

                <div className="bg-background-100 rounded-lg p-6 text-center">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-50 text-primary-500 mx-auto mb-4">
                    <i className="ri-map-pin-line text-xl"></i>
                  </div>
                  <h3 className="text-sm font-medium text-foreground-900 mb-1">公司地址</h3>
                  <p className="text-sm text-foreground-600">韩国首尔特别市江南区</p>
                  <p className="text-xs text-foreground-400 mt-1">Teheran-ro 152</p>
                </div>
              </div>

              <div className="mt-10 bg-background-100 rounded-lg p-8">
                <h3 className="text-base font-medium text-foreground-900 mb-6 text-center">发送消息</h3>
                <form
                  ref={formRef}
                  className="max-w-lg mx-auto space-y-4"
                  data-readdy-form="true"
                  onSubmit={handleSubmit}
                >
                  <div>
                    <label htmlFor="name" className="block text-sm text-foreground-700 mb-1">姓名</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors duration-200"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm text-foreground-700 mb-1">邮箱</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors duration-200"
                      placeholder="请输入您的邮箱"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm text-foreground-700 mb-1">留言内容</label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors duration-200 resize-none"
                      placeholder="请输入您的留言内容（500字以内）"
                    ></textarea>
                  </div>
                  <div className="honeypot-field">
                    <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                  </div>
                  {formStatus === 'success' ? (
                    <div className="text-center py-4">
                      <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 mx-auto mb-3">
                        <i className="ri-check-line text-xl"></i>
                      </div>
                      <p className="text-sm text-foreground-700 font-medium">消息已发送成功！</p>
                      <p className="text-xs text-foreground-500 mt-1">我们会尽快回复您，感谢您的留言。</p>
                    </div>
                  ) : (
                    <button
                      type="submit"
                      disabled={formStatus === 'submitting'}
                      className="w-full py-3 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {formStatus === 'submitting' ? '发送中...' : '发送消息'}
                    </button>
                  )}
                  {formStatus === 'error' && formErrorMsg && (
                    <p className="text-xs text-center text-red-500 mt-2">{formErrorMsg}</p>
                  )}
                </form>
              </div>
            </div>
          </section>

          <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100" aria-labelledby="about-faq-heading">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-8">
                <h2 id="about-faq-heading" className="text-2xl md:text-3xl font-heading text-foreground-950 mb-3">
                  常见问题
                </h2>
                <p className="text-sm text-foreground-500">以下是读者最常咨询的问题</p>
              </div>

              <div className="space-y-3">
                {siteFaqs.map((faq) => (
                  <div
                    key={faq.id}
                    className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden"
                  >
                    <button
                      onClick={() => setOpenFaq(openFaq === faq.id ? null : faq.id)}
                      className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer hover:bg-background-50/80 transition-colors duration-200"
                      aria-expanded={openFaq === faq.id}
                    >
                      <h3 className="text-sm md:text-base font-medium text-foreground-900 pr-4">
                        {faq.question}
                      </h3>
                      <i
                        className={`ri-arrow-down-s-line text-foreground-400 transition-transform duration-300 flex-shrink-0 ${
                          openFaq === faq.id ? 'rotate-180' : ''
                        }`}
                      ></i>
                    </button>
                    {openFaq === faq.id && (
                      <div className="px-5 pb-4">
                        <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="px-4 md:px-6 py-10 text-center">
            <div className="max-w-xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-950 mb-4">
                立即开启韩漫阅读之旅
              </h2>
              <p className="text-sm text-foreground-500 mb-6">
                海量韩漫大全等你来探索，加入500万+读者的行列！
              </p>
              <Link
                to="/category/hot"
                className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-all duration-300 whitespace-nowrap"
              >
                <i className="ri-fire-line"></i>
                热门推荐
              </Link>
            </div>
          </section>
        </div>
      </main>
    </>
  );
}